import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { updateWatchlistData } from "../../../network/service";
import { fetchWatchlistShows } from "../../MyList/service";
import { getMyListData } from "../../../Redux/MyList/MyListSlice";
import { getBannerLoading } from "../../../Redux/BannerLoading/BannerLoadingSlice";
import { getMoreInfo } from "../../../Redux/MoreInfo/moreInfoSlice";
import { getTrailer } from "../../Trailer/service";

export default function BannerUtil() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);
  const user = useSelector((state) => state?.user?.value);
  
  const appInfo = {
    projectDetails: projectInfo,
    accessToken: accessToken,
  };

  const afterImageLoad = () => {
    dispatch(
      getBannerLoading({
        bannerLoading: true
      })
    );
  };

  const replaceImage = (error) => {
    error.target.src = projectInfo?.projectConfig?.config?.PLACEHOLDER_IMAGE;
  };

  const fetchWatchlist = async () => {
    const response = await fetchWatchlistShows(appInfo);
    if (response?.status === 200) {
      dispatch(
        getMyListData({
          myList: response?.data?.data,
        })
      );
    }
    return response?.data?.data;
  };

  const updateWatchlist = async (showId, flag, setWatchlistStatus, setShowNotification = null) => {
    if (!user) {
      navigate("/login");
      return null;
    }

    try {
      const response = await updateWatchlistData(appInfo, showId, flag);

      if (response?.status === 200) {
        fetchWatchlist();
        setWatchlistStatus((prev) => ({ ...prev, [showId]: flag }));

        if (flag === 1) {
          toast.success("Added to watchlist", { position: "bottom-center" });
        } else {
          toast.success("Removed from watchlist", { position: "bottom-center" });
        }
      }
      return response;
    } catch (err) {
      console.error("Error updating watchlist:", err);
      toast.error("Unable to update watchlist", { position: "bottom-center" });
      return null;
    }
  };

  const addToWatchlist = (showId, setWatchlistStatus, setShowNotification = null) => {
    if (user) {
      return updateWatchlist(showId, 1, setWatchlistStatus, setShowNotification);
    } else {
      navigate("/login");
      return null;
    }
  };

  const removeFromWatchlist = (showId, setWatchlistStatus, setShowNotification = null) => {
    return updateWatchlist(showId, 0, setWatchlistStatus, setShowNotification);
  };

  const handleWatchlistToggle = (showId, watchlistStatus, setWatchlistStatus, setShowNotification = null, setCurrentShowId = null) => {
    if (setCurrentShowId) {
      setCurrentShowId(showId);
    }
    
    if (watchlistStatus[showId]) {
      return removeFromWatchlist(showId, setWatchlistStatus, setShowNotification);
    } else {
      return addToWatchlist(showId, setWatchlistStatus, setShowNotification);
    }
  };

  const playClickHandler = (item, options = {}) => {
    const { isSeeMore = false, showMoreInfo = false } = options;

    if (item?.type === "GENRE") {
      navigate(`/category/${item?.vanity_url}`);
      return;
    }

    if (item?.type === "HOME") {
      navigate(`/genre/${item?.vanity_url}`);
      return;
    }

    if (item?.type === "FASTCHANNEL") {
      const channelId = item?.channel_id || item?.uuid || item?.id || item?.show_id || item?.event_id || null;
      const stateObj = { channel_id: channelId, channelId: channelId, homelive: true };
      if (projectInfo?.projectConfig?.config?.MULTI_CHANNELS_REQUIRED === "true") {
        navigate(`/live-channels?c=${channelId}`, { state: stateObj });
      } else {
        navigate("/live");
      }
      return;
    }

    if (item?.type === "LIVE_EVENT" || item?.type === "UPCOMING_EVENT") {
      navigate(`/event/${item?.vanity_url ? item?.vanity_url : item?.event_id}`, {
        state: { showId: item?.event_id },
      });
      return;
    }

    if (item?.show_id) {
      if (showMoreInfo) {
        navigate(`/show-details/${item?.vanity_url ? item?.vanity_url : item?.show_id}`, {
          state: { 
            showId: item?.show_id,
            isSeeMore
          },
        });
        
        dispatch(
          getMoreInfo({
            moreInfo: true
          })
        );
      } 
      else if (item?.video_vanity_url) {
        navigate(`/videos/${item?.video_vanity_url}`, {
          state: { 
            showId: item?.show_id,
            isSeeMore,
            videoDetails: item
          },
        });
      } else {
        navigate(`/show-details/${item?.vanity_url ? item?.vanity_url : item?.show_id}`, {
          state: { 
            showId: item?.show_id,
            isSeeMore
          },
        });
      }
    } 
    else if (item?.event_id) {
      navigate(`/event/${item?.vanity_url ? item?.vanity_url : item?.event_id}`, {
        state: { showId: item?.event_id },
      });
    } 
    else if (item?.type === "VIDEO") {
      navigate(`/videos/${item?.vanity_url}`, {
        state: { videoDetails: item },
      });
    }
    else if (item?.teaser_vanity_url) {
      navigate(`/trailer/${item?.teaser_vanity_url}`, {
        state: { teaser_vanity_url: item.teaser_vanity_url },
      });
    }
  };

  const subscriptionCheck = (userSub, videoSub) => {
    const hasMatch = userSub?.some((user) =>
      videoSub?.some((video) => user.sub_id === video.subscription_id)
    );
    return hasMatch;
  };

  const handleSignIn = () => {
    navigate("/login");
  };

  const truncateDescription = (description, maxLength) => {
    if (!description) return "";
    
    if (description.length <= maxLength) {
      return description;
    }
    
    const lastSpace = description.lastIndexOf(" ", maxLength);
    if (lastSpace === -1) {
      return description.substring(0, maxLength) + "...";
    }
    
    return description.substring(0, lastSpace) + "...";
  };

  const trailerButtonHandle = async (teaser, setTrailerLink, setToggler, toggler = false) => {
    try {
      const response = await getTrailer(appInfo, teaser);
      setTrailerLink(response?.data?.data?.teaser);
      setToggler(!toggler);
      return response;
    } catch (err) {
      console.error("Error fetching trailer:", err);
      return null;
    }
  };


  return {
    replaceImage,
    afterImageLoad,
    fetchWatchlist,
    updateWatchlist,
    addToWatchlist,
    removeFromWatchlist,
    handleWatchlistToggle,
    playClickHandler,
    trailerButtonHandle,   
    subscriptionCheck,
    handleSignIn,
    truncateDescription,
  };
}